import { Fragment, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";

import { ISetting } from "../../models/Setting";
import AdminSettingsValue from "./AdminSettingsValue";

const colHelper = createColumnHelper<ISetting>();

export default function AdminSettingsTable({
  settings,
}: {
  settings: ISetting[];
}) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "key", desc: false },
  ]);

  const cols = [
    colHelper.accessor("key", {
      header: "Name",
      size: 10,
      maxSize: 10,
      sortingFn: "auto",
      cell: (info) => {
        const idx = info.getValue().indexOf(":");
        const displayKey = idx === -1 ? info.getValue() : info.getValue().slice(idx + 1).trim();
        return displayKey;
      }
    }),
    colHelper.accessor("value", {
      header: "Value",
      size: 40,
      maxSize: 40,
      cell: (info) => <AdminSettingsValue setting={info.row.original} />,
    }),
  ];

  const table = useReactTable({
    data: settings,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className="mt-4 bg-white overflow-auto">
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: " ▲",
                          desc: " ▼",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, i) => {
              let rowCells = row.getVisibleCells();
              return (
                <Fragment key={i}>
                  <tr
                    key={row.id}
                  >
                    {rowCells.map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 py-3 border text-sm"
                        width={`${cell.column.getSize()}%`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <></>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
